// accounts.ts -- multi-account session storage for the dev experience.
//
// Ported from gigbook's pattern (client/src/utils/api.js). Each saved
// account holds a JWT + display metadata in localStorage. The active
// account's token is mirrored to a separate "token" key so api.ts can
// add it as the Authorization: Bearer header on every request.
//
// Why this exists: fdn-app's production auth path is httpOnly cookies
// stamped by /api/auth/exchange. That works fine for one user per
// browser tab, but testing I11's symmetric DM view ("Alice sees admin
// as her recipient persona") requires logging in as two users without
// constant logout/login. Cookies can't be swapped from JS; localStorage
// tokens can.
//
// The BFF's auth middleware already accepts Authorization: Bearer
// before falling back to the cookie (server/src/auth.ts extractToken),
// so this is a pure client-side addition. Dev convenience first; can
// graduate to a real account-switcher with multi-cookie support if
// needed for production.
const ACCOUNTS_KEY = 'fdn:accounts';
const ACTIVE_ACCOUNT_KEY = 'fdn:active-account';
const TOKEN_KEY = 'fdn:token';
function parse(raw, fallback) {
    if (!raw)
        return fallback;
    try {
        return JSON.parse(raw);
    }
    catch {
        return fallback;
    }
}
function isAccount(v) {
    if (!v || typeof v !== 'object')
        return false;
    const a = v;
    return (typeof a.accountId === 'string' && a.accountId.length > 0 &&
        typeof a.token === 'string' && a.token.length > 0);
}
function sortAccounts(a, b) {
    // Most-recent first.
    return (b.lastUsedAt ?? '').localeCompare(a.lastUsedAt ?? '');
}
export function getAccounts() {
    const raw = parse(localStorage.getItem(ACCOUNTS_KEY), []);
    if (!Array.isArray(raw))
        return [];
    return raw.filter(isAccount).sort(sortAccounts);
}
export function setAccounts(accounts) {
    const list = accounts.filter(isAccount).sort(sortAccounts);
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list));
}
export function getActiveAccountId() {
    const raw = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
    return raw && raw.length > 0 ? raw : null;
}
export function setActiveAccountId(accountId) {
    if (accountId) {
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
    }
    else {
        localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    }
}
/** The active account's JWT, or null if no account is active. */
export function getActiveToken() {
    return localStorage.getItem(TOKEN_KEY);
}
function setToken(token) {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    }
    else {
        localStorage.removeItem(TOKEN_KEY);
    }
}
/**
 * Insert or update a saved account, mark it active, mirror its token
 * to the active-token slot. Called after a successful login.
 */
export function upsertAccountSession(input) {
    const next = {
        accountId: input.userId,
        userId: input.userId,
        token: input.token,
        email: input.email,
        name: input.name || input.email,
        lastUsedAt: new Date().toISOString(),
    };
    const accounts = getAccounts();
    const idx = accounts.findIndex((a) => a.accountId === next.accountId);
    if (idx >= 0) {
        accounts[idx] = { ...accounts[idx], ...next };
    }
    else {
        accounts.push(next);
    }
    setAccounts(accounts);
    setActiveAccountId(next.accountId);
    setToken(next.token);
    return next;
}
/**
 * Switch the active account to one already saved. Returns the new
 * active account, or null if accountId isn't found.
 */
export function switchActiveAccount(accountId) {
    const account = getAccounts().find((a) => a.accountId === accountId);
    if (!account)
        return null;
    // Bump lastUsedAt on switch so the dropdown sorts by recency.
    const accounts = getAccounts().map((a) => (a.accountId === accountId
        ? { ...a, lastUsedAt: new Date().toISOString() }
        : a));
    setAccounts(accounts);
    setActiveAccountId(accountId);
    setToken(account.token);
    return account;
}
/**
 * Remove the currently-active account, fall back to the next saved
 * account if any. Returns the new active account, or null if none.
 */
export function removeActiveAccountSession() {
    const activeId = getActiveAccountId();
    if (!activeId) {
        setToken(null);
        return null;
    }
    const remaining = getAccounts().filter((a) => a.accountId !== activeId);
    setAccounts(remaining);
    if (remaining.length > 0) {
        const next = remaining[0];
        setActiveAccountId(next.accountId);
        setToken(next.token);
        return next;
    }
    setActiveAccountId('');
    setToken(null);
    return null;
}
/** Wipe everything. Used by "Logout all". */
export function clearAllAccountSessions() {
    localStorage.removeItem(ACCOUNTS_KEY);
    localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    localStorage.removeItem(TOKEN_KEY);
}
